/**
 * B2BReturnCatalogPage — Catalog-style return document page.
 *
 * Same look & feel as B2BCatalogPage but reversed: instead of ordering,
 * the user picks products to return.  Each product shows the client's
 * warehouse balance and the user cannot return more than what's in stock.
 * Submits via the existing submit_bulk_return RPC.
 */

import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/AuthContext';
import { useB2BUser, type AgentClientRecord } from './B2BUserContext';
import { useB2BTerms } from './B2BTermsContext';
import {
  Loader as Loader2,
  CircleAlert as AlertCircle,
  Search,
  LayoutGrid,
  List,
  ShoppingBag,
  Plus,
  Minus,
  RotateCcw,
  CircleCheck as CheckCircle2,
  Store,
  ChevronDown,
  Building2,
  X as XIcon,
  PackageX,
  ListFilter as Filter,
  FolderTree,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  Trash2,
  Package,
  Send,
  Zap,
} from 'lucide-react';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ReturnProduct {
  id: string;
  company_id: string;
  sku: string;
  name: string;
  price: number;
  image_url: string | null;
  description: string | null;
  group_name: string | null;
  supplier_name: string | null;
  /** Stock in client's warehouse */
  stock_qty: number;
}

interface ReturnCartItem {
  product: ReturnProduct;
  qty: number;
  reason: string;
}

interface SupplierOption { id: string; name: string; }
interface GroupOption { id: string; name: string; parent_id: string | null; }

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatPrice(n: number) {
  return `₪${n.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ─── Quantity Stepper (capped by max) ────────────────────────────────────────

function QtyStepper({
  value, onChange, min = 1, max,
}: {
  value: number;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
}) {
  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - 1))}
        className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200
                   text-gray-500 hover:bg-gray-100 active:scale-90 transition-all"
      >
        <Minus className="w-3 h-3" />
      </button>
      <input
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={e => {
          const n = parseInt(e.target.value, 10);
          if (!isNaN(n) && n >= min) {
            onChange(max != null ? Math.min(n, max) : n);
          }
        }}
        className="w-10 h-7 text-center text-sm font-bold border border-gray-200
                   rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-300
                   focus:border-transparent"
      />
      <button
        type="button"
        onClick={() => {
          if (max != null && value >= max) return;
          onChange(value + 1);
        }}
        disabled={max != null && value >= max}
        className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200
                   text-gray-500 hover:bg-gray-100 active:scale-90 transition-all
                   disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <Plus className="w-3 h-3" />
      </button>
    </div>
  );
}

// ─── Stock Badge ─────────────────────────────────────────────────────────────

function StockBadge({ qty }: { qty: number }) {
  if (qty <= 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full
                        bg-gray-100 text-gray-400 text-[10px] font-semibold">
        <Package className="w-3 h-3" />
        אין במלאי
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full
                      bg-emerald-50 text-emerald-700 text-[10px] font-semibold border border-emerald-100">
      <Package className="w-3 h-3" />
      במלאי: {qty}
    </span>
  );
}

// ─── Supplier Badge ──────────────────────────────────────────────────────────

function SupplierBadge({ name }: { name: string | null }) {
  if (!name) return null;
  return (
    <div className="flex items-center gap-1 min-w-0">
      <Store className="w-3 h-3 shrink-0 text-gray-400" />
      <span className="text-[10px] text-gray-400 font-medium truncate leading-none" title={name}>
        {name}
      </span>
    </div>
  );
}

// ─── Product Image ───────────────────────────────────────────────────────────

function ProductImage({ src, alt, className }: { src: string | null; alt: string; className?: string }) {
  return src ? (
    <img src={src} alt={alt} className={`object-cover ${className ?? ''}`} loading="lazy"
         onError={e => { (e.target as HTMLImageElement).src = ''; }} />
  ) : (
    <div className={`flex items-center justify-center bg-gray-100 ${className ?? ''}`}>
      <ShoppingBag className="w-1/3 h-1/3 text-gray-300" />
    </div>
  );
}

// ─── Return Button (orange themed) ───────────────────────────────────────────

function ReturnButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  const [flash, setFlash] = useState(false);
  const handle = () => {
    onClick();
    setFlash(true);
    setTimeout(() => setFlash(false), 900);
  };

  if (disabled) {
    return (
      <button type="button" disabled
        className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold
                   bg-gray-100 text-gray-400 cursor-not-allowed shrink-0">
        <PackageX className="w-3.5 h-3.5" />
        אין במלאי
      </button>
    );
  }

  return (
    <button type="button" onClick={handle}
      className={[
        'flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold',
        'transition-all duration-200 active:scale-95 shrink-0',
        flash
          ? 'bg-green-500 text-white'
          : 'bg-orange-500 hover:bg-orange-600 text-white',
      ].join(' ')}>
      {flash
        ? <><CheckCircle2 className="w-3.5 h-3.5" />נוסף!</>
        : <><RotateCcw className="w-3.5 h-3.5" />החזר</>}
    </button>
  );
}

// ─── Grid Card ───────────────────────────────────────────────────────────────

function GridCard({
  product, qty, onQtyChange, onAdd, vatRate, isVatLiable, onDetail,
}: {
  product: ReturnProduct;
  qty: number;
  onQtyChange: (n: number) => void;
  onAdd: () => void;
  vatRate: number;
  isVatLiable: boolean;
  onDetail?: () => void;
}) {
  const noStock = product.stock_qty <= 0;

  return (
    <div className={`bg-white rounded-2xl shadow-sm border flex flex-col hover:shadow-md
                    transition-shadow cursor-pointer
                    ${noStock ? 'border-gray-100 opacity-70' : 'border-gray-100'}`}
         onClick={onDetail}>
      <div className="aspect-[4/3] w-full relative overflow-hidden rounded-t-2xl">
        <ProductImage src={product.image_url} alt={product.name} className="w-full h-full" />
        {noStock && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-[1px]">
            <span className="flex items-center gap-1 px-2.5 py-1 bg-gray-700/80 text-white text-[10px] font-bold rounded-full">
              <PackageX className="w-3 h-3" />
              אין במלאי
            </span>
          </div>
        )}
      </div>
      <div className="p-3 flex flex-col gap-2 flex-1">
        {product.group_name && (
          <span className="text-[10px] font-semibold text-primary-500 uppercase tracking-wide">
            {product.group_name}
          </span>
        )}
        <p className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 flex-1">
          {product.name}
        </p>
        <div className="flex items-center justify-between gap-1 min-w-0">
          <SupplierBadge name={product.supplier_name} />
          <p className="text-[10px] text-gray-300 font-mono shrink-0" dir="ltr">{product.sku}</p>
        </div>
        <div className="flex items-center justify-between gap-2">
          <p className="text-base font-bold text-gray-900">{formatPrice(product.price)}</p>
          <StockBadge qty={product.stock_qty} />
        </div>
        {isVatLiable && vatRate > 0 && (
          <p className="text-[11px] text-gray-400 -mt-1">
            כולל מע״מ: {formatPrice(product.price * (1 + vatRate / 100))}
          </p>
        )}
        <div className="flex items-center justify-between gap-2 mt-auto pt-1" onClick={e => e.stopPropagation()}>
          <QtyStepper value={qty} onChange={onQtyChange} min={1} max={product.stock_qty > 0 ? product.stock_qty : undefined} />
          <ReturnButton onClick={onAdd} disabled={noStock} />
        </div>
      </div>
    </div>
  );
}

// ─── List Row ────────────────────────────────────────────────────────────────

function ListRow({
  product, qty, onQtyChange, onAdd, vatRate, isVatLiable, onDetail,
}: {
  product: ReturnProduct;
  qty: number;
  onQtyChange: (n: number) => void;
  onAdd: () => void;
  vatRate: number;
  isVatLiable: boolean;
  onDetail?: () => void;
}) {
  const noStock = product.stock_qty <= 0;

  return (
    <div className={`bg-white rounded-2xl shadow-sm border border-gray-100
                    flex items-center gap-3 p-3 hover:shadow-md transition-shadow cursor-pointer
                    ${noStock ? 'opacity-70' : ''}`}
         onClick={onDetail}>
      <div className="w-16 h-16 shrink-0 rounded-xl overflow-hidden relative">
        <ProductImage src={product.image_url} alt={product.name} className="w-full h-full" />
        {noStock && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/60">
            <PackageX className="w-5 h-5 text-gray-500" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0 space-y-0.5">
        {product.group_name && (
          <p className="text-[10px] font-semibold text-primary-500 uppercase tracking-wide">
            {product.group_name}
          </p>
        )}
        <p className="text-sm font-semibold text-gray-900 truncate">{product.name}</p>
        <div className="flex items-center gap-2 min-w-0">
          <SupplierBadge name={product.supplier_name} />
          <p className="text-[10px] text-gray-300 font-mono shrink-0" dir="ltr">{product.sku}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-bold text-gray-800">{formatPrice(product.price)}</p>
          {isVatLiable && vatRate > 0 && (
            <span className="text-[10px] text-gray-400">
              כולל מע״מ: {formatPrice(product.price * (1 + vatRate / 100))}
            </span>
          )}
          <StockBadge qty={product.stock_qty} />
        </div>
      </div>
      <div className="flex flex-col items-end gap-2 shrink-0" onClick={e => e.stopPropagation()}>
        <QtyStepper value={qty} onChange={onQtyChange} min={1} max={product.stock_qty > 0 ? product.stock_qty : undefined} />
        <ReturnButton onClick={onAdd} disabled={noStock} />
      </div>
    </div>
  );
}

// ─── Product Detail Modal ────────────────────────────────────────────────────

function ProductDetailModal({
  product, images, onClose, qty, onQtyChange, onAdd, vatRate, isVatLiable,
}: {
  product: ReturnProduct;
  images: { image_url: string; alt_text: string | null; position: number }[];
  onClose: () => void;
  qty: number;
  onQtyChange: (n: number) => void;
  onAdd: () => void;
  vatRate: number;
  isVatLiable: boolean;
}) {
  const allImages = images.length > 0
    ? images.sort((a, b) => a.position - b.position).map(i => i.image_url)
    : product.image_url ? [product.image_url] : [];
  const [idx, setIdx] = useState(0);
  const noStock = product.stock_qty <= 0;

  const prev = () => setIdx(i => (i <= 0 ? allImages.length - 1 : i - 1));
  const next = () => setIdx(i => (i >= allImages.length - 1 ? 0 : i + 1));

  const touchStart = useRef<number | null>(null);
  const handleTouchStart = (e: React.TouchEvent) => { touchStart.current = e.touches[0]?.clientX ?? null; };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStart.current === null) return;
    const endX = e.changedTouches[0]?.clientX ?? touchStart.current;
    const diff = touchStart.current - endX;
    if (Math.abs(diff) > 50) { diff > 0 ? next() : prev(); }
    touchStart.current = null;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4 py-6"
         onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <div className="relative aspect-square bg-gray-50 rounded-t-2xl overflow-hidden"
             onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
          {allImages.length > 0 ? (
            <img src={allImages[idx]} alt={product.name} className="w-full h-full object-contain" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageIcon className="w-16 h-16 text-gray-200" />
            </div>
          )}
          {allImages.length > 1 && (
            <>
              <button onClick={prev}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center
                           rounded-full bg-white/80 shadow hover:bg-white transition-colors">
                <ChevronLeft className="w-4 h-4 text-gray-700" />
              </button>
              <button onClick={next}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center
                           rounded-full bg-white/80 shadow hover:bg-white transition-colors">
                <ChevronRight className="w-4 h-4 text-gray-700" />
              </button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
                {allImages.map((_, i) => (
                  <button key={i} onClick={() => setIdx(i)}
                    className={`w-2 h-2 rounded-full transition-all ${i === idx ? 'bg-orange-500 scale-125' : 'bg-gray-300'}`} />
                ))}
              </div>
            </>
          )}
          <button onClick={onClose}
            className="absolute top-3 left-3 w-8 h-8 flex items-center justify-center rounded-full
                       bg-white/80 shadow hover:bg-white transition-colors">
            <XIcon className="w-4 h-4 text-gray-700" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {product.group_name && (
            <span className="text-xs font-semibold text-primary-500 uppercase tracking-wide">
              {product.group_name}
            </span>
          )}
          <h2 className="text-lg font-bold text-gray-900 leading-snug">{product.name}</h2>
          <div className="flex items-center gap-3 flex-wrap">
            <SupplierBadge name={product.supplier_name} />
            <span className="text-xs text-gray-300 font-mono" dir="ltr">{product.sku}</span>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xl font-bold text-gray-900">{formatPrice(product.price)}</span>
            {isVatLiable && vatRate > 0 && (
              <span className="text-sm text-gray-400">
                כולל מע״מ: {formatPrice(product.price * (1 + vatRate / 100))}
              </span>
            )}
          </div>
          <StockBadge qty={product.stock_qty} />

          {noStock && (
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-500">
              <PackageX className="w-4 h-4" />
              אין במלאי — לא ניתן להחזיר
            </div>
          )}

          {product.description && (
            <div className="border-t border-gray-100 pt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">תיאור מוצר</h3>
              <div className="text-sm text-gray-600 leading-relaxed prose prose-sm max-w-none
                             [&_ul]:list-disc [&_ul]:mr-4 [&_ol]:list-decimal [&_ol]:mr-4
                             [&_li]:my-0.5 [&_p]:my-1 [&_strong]:font-semibold
                             [&_a]:text-primary-600 [&_a]:underline"
                   dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(product.description) }} />
            </div>
          )}

          {!noStock && (
            <div className="flex items-center justify-between gap-3 border-t border-gray-100 pt-4">
              <QtyStepper value={qty} onChange={onQtyChange} min={1} max={product.stock_qty} />
              <ReturnButton onClick={onAdd} disabled={noStock} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Supplier Filter ─────────────────────────────────────────────────────────

function SupplierFilter({
  suppliers, selectedSupplierId, onChange,
}: {
  suppliers: SupplierOption[];
  selectedSupplierId: string | null;
  onChange: (id: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const selectedName = selectedSupplierId
    ? (suppliers.find(s => s.id === selectedSupplierId)?.name ?? 'ספק')
    : 'כל הספקים';
  const isFiltered = !!selectedSupplierId;

  if (suppliers.length === 0) {
    return (
      <div className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl border text-sm font-medium
                     bg-gray-50 border-gray-200 text-gray-400 whitespace-nowrap shrink-0 select-none">
        <Building2 className="w-4 h-4 shrink-0 text-gray-300" />
        <span className="hidden sm:block">ספק</span>
      </div>
    );
  }

  return (
    <div ref={ref} className="relative shrink-0">
      <button onClick={() => setOpen(o => !o)}
        className={[
          'flex items-center gap-1.5 px-3 py-2.5 rounded-xl border text-sm font-medium',
          'transition-colors focus:outline-none focus:ring-2 focus:ring-orange-300 whitespace-nowrap',
          isFiltered
            ? 'bg-orange-500 border-orange-500 text-white'
            : open
              ? 'bg-orange-50 border-orange-300 text-orange-700'
              : 'bg-white border-gray-200 text-gray-600 hover:border-orange-300 hover:text-orange-700',
        ].join(' ')}>
        <Building2 className="w-4 h-4 shrink-0" />
        <span className="hidden sm:block max-w-[90px] truncate">{selectedName}</span>
        {isFiltered ? (
          <button type="button" onClick={e => { e.stopPropagation(); onChange(null); }}
            className="p-0.5 rounded hover:bg-white/20 transition-colors">
            <XIcon className="w-3 h-3" />
          </button>
        ) : (
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
        )}
      </button>

      {open && (
        <div className="absolute top-full mt-1 right-0 z-40 min-w-[180px] max-w-xs
                       bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden">
          <div className="px-3 pt-2.5 pb-1.5 border-b border-gray-100">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">סינון לפי ספק</p>
          </div>
          <div className="py-1">
            <button onClick={() => { onChange(null); setOpen(false); }}
              className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-right transition-colors
                ${!selectedSupplierId ? 'bg-orange-50 text-orange-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}>
              <Store className="w-3.5 h-3.5 shrink-0 text-gray-300" />
              <span className="flex-1 text-right">כל הספקים</span>
              {!selectedSupplierId && <CheckCircle2 className="w-3.5 h-3.5 text-orange-500 shrink-0" />}
            </button>
            {suppliers.map(s => (
              <button key={s.id} onClick={() => { onChange(s.id); setOpen(false); }}
                className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-right transition-colors
                  ${s.id === selectedSupplierId ? 'bg-orange-50 text-orange-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}>
                <Building2 className="w-3.5 h-3.5 shrink-0 text-gray-300" />
                <span className="flex-1 truncate text-right">{s.name}</span>
                {s.id === selectedSupplierId && <CheckCircle2 className="w-3.5 h-3.5 text-orange-500 shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Group Filter ────────────────────────────────────────────────────────────

function GroupFilter({
  groups, selectedGroupIds, onChange,
}: {
  groups: GroupOption[];
  selectedGroupIds: Set<string>;
  onChange: (ids: Set<string>) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (groups.length === 0) return null;
  const count = selectedGroupIds.size;
  const isFiltered = count > 0;
  const toggleGroup = (id: string) => {
    const next = new Set(selectedGroupIds);
    next.has(id) ? next.delete(id) : next.add(id);
    onChange(next);
  };
  const clearAll = () => onChange(new Set());

  return (
    <div ref={ref} className="relative shrink-0">
      <button onClick={() => setOpen(o => !o)}
        className={[
          'flex items-center gap-1.5 px-3 py-2.5 rounded-xl border text-sm font-medium',
          'transition-colors focus:outline-none focus:ring-2 focus:ring-orange-300 whitespace-nowrap',
          isFiltered
            ? 'bg-orange-500 border-orange-500 text-white'
            : open
              ? 'bg-orange-50 border-orange-300 text-orange-700'
              : 'bg-white border-gray-200 text-gray-600 hover:border-orange-300 hover:text-orange-700',
        ].join(' ')}>
        <Filter className="w-4 h-4 shrink-0" />
        <span className="hidden sm:block">{isFiltered ? `קבוצות (${count})` : 'קבוצה'}</span>
        {isFiltered ? (
          <button type="button" onClick={e => { e.stopPropagation(); clearAll(); }}
            className="p-0.5 rounded hover:bg-white/20 transition-colors">
            <XIcon className="w-3 h-3" />
          </button>
        ) : (
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
        )}
      </button>

      {open && (
        <div className="absolute top-full mt-1 right-0 z-40 min-w-[200px] max-w-xs
                       bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden">
          <div className="px-3 pt-2.5 pb-1.5 border-b border-gray-100 flex items-center justify-between">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">סינון לפי קבוצה</p>
            {isFiltered && (
              <button onClick={clearAll}
                className="text-[10px] text-orange-500 hover:text-orange-700 font-semibold transition-colors">
                נקה הכל
              </button>
            )}
          </div>
          <div className="py-1 max-h-60 overflow-y-auto">
            {groups.map(g => {
              const isSelected = selectedGroupIds.has(g.id);
              return (
                <button key={g.id} onClick={() => toggleGroup(g.id)}
                  className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-right transition-colors
                    ${isSelected ? 'bg-orange-50 text-orange-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}
                  style={{ paddingRight: g.parent_id ? '28px' : undefined }}>
                  <FolderTree className="w-3.5 h-3.5 shrink-0 text-amber-400" />
                  <span className="flex-1 truncate text-right">{g.name}</span>
                  {isSelected && <CheckCircle2 className="w-3.5 h-3.5 text-orange-500 shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Floating Return Cart Bar ────────────────────────────────────────────────

function FloatingCartBar({
  totalItems, totalPrice, onClick,
}: {
  totalItems: number;
  totalPrice: number;
  onClick: () => void;
}) {
  if (totalItems === 0) return null;
  return (
    <div className="fixed bottom-4 left-4 right-4 z-40 max-w-lg mx-auto">
      <button onClick={onClick}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 rounded-2xl
                   bg-orange-500 text-white shadow-xl hover:bg-orange-600
                   transition-colors active:scale-[0.98]">
        <div className="flex items-center gap-2">
          <RotateCcw className="w-5 h-5" />
          <span className="font-bold text-sm">{totalItems} פריטים להחזרה</span>
        </div>
        <span className="font-bold text-sm">{formatPrice(totalPrice)}</span>
      </button>
    </div>
  );
}

// ─── Expanded Return Cart ────────────────────────────────────────────────────

function ExpandedReturnCart({
  items, onClose, onSetQty, onSetReason, onRemove, onClear,
  onSubmit, submitting, submitError, vatRate, isVatLiable,
}: {
  items: ReturnCartItem[];
  onClose: () => void;
  onSetQty: (productId: string, qty: number) => void;
  onSetReason: (productId: string, reason: string) => void;
  onRemove: (productId: string) => void;
  onClear: () => void;
  onSubmit: (instant: boolean) => void;
  submitting: boolean;
  submitError: string | null;
  vatRate: number;
  isVatLiable: boolean;
}) {
  const totalItems = items.reduce((s, i) => s + i.qty, 0);
  const totalPrice = items.reduce((s, i) => s + i.product.price * i.qty, 0);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center"
         onClick={onClose}>
      <div className="bg-white w-full max-w-lg max-h-[85vh] rounded-t-3xl sm:rounded-2xl shadow-2xl
                     flex flex-col overflow-hidden"
           onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <RotateCcw className="w-5 h-5 text-orange-500" />
            <h2 className="text-lg font-bold text-gray-900">סל החזרות</h2>
            <span className="text-sm text-gray-400">({totalItems} פריטים)</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClear}
              className="text-xs text-red-500 hover:text-red-700 font-semibold transition-colors">
              נקה הכל
            </button>
            <button onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors">
              <XIcon className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-3">
          {items.map(item => (
            <div key={item.product.id}
              className="flex items-start gap-3 bg-gray-50 rounded-xl p-3">
              <div className="w-12 h-12 shrink-0 rounded-lg overflow-hidden">
                <ProductImage src={item.product.image_url} alt={item.product.name} className="w-full h-full" />
              </div>
              <div className="flex-1 min-w-0 space-y-1">
                <p className="text-sm font-semibold text-gray-900 truncate">{item.product.name}</p>
                <div className="flex items-center gap-2">
                  <p className="text-xs text-gray-500">{formatPrice(item.product.price)} × {item.qty}</p>
                  <p className="text-xs font-bold text-gray-700">= {formatPrice(item.product.price * item.qty)}</p>
                </div>
                <StockBadge qty={item.product.stock_qty} />
                <div className="flex items-center gap-2 pt-1">
                  <QtyStepper value={item.qty}
                    onChange={n => onSetQty(item.product.id, n)}
                    min={1} max={item.product.stock_qty} />
                  <button onClick={() => onRemove(item.product.id)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-red-400
                               hover:bg-red-50 hover:text-red-600 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <input type="text" placeholder="סיבת החזרה (אופציונלי)"
                  value={item.reason} onChange={e => onSetReason(item.product.id, e.target.value)}
                  className="w-full mt-1 px-2.5 py-1.5 text-xs rounded-lg border border-gray-200
                             focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-transparent
                             placeholder:text-gray-300" />
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 px-5 py-4 space-y-3">
          {submitError && (
            <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200
                           rounded-xl text-xs text-red-700">
              <AlertCircle className="w-3.5 h-3.5 shrink-0" />
              {submitError}
            </div>
          )}

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">סה״כ זיכוי</span>
            <span className="text-lg font-bold text-gray-900">{formatPrice(totalPrice)}</span>
          </div>

          {isVatLiable && vatRate > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">כולל מע״מ</span>
              <span className="text-sm text-gray-500">{formatPrice(totalPrice * (1 + vatRate / 100))}</span>
            </div>
          )}

          <div className="flex gap-2">
            <button onClick={() => onSubmit(false)} disabled={submitting || items.length === 0}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm
                         bg-orange-500 text-white hover:bg-orange-600 transition-colors
                         disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              שלח תעודת החזרה
            </button>
            <button onClick={() => onSubmit(true)} disabled={submitting || items.length === 0}
              title="שלח והחזר מיידית (ללא אישור)"
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold text-sm
                         bg-amber-500 text-white hover:bg-amber-600 transition-colors
                         disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]">
              <Zap className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Success Screen ──────────────────────────────────────────────────────────

function SuccessScreen({
  result, cart, clientName, onDone,
}: {
  result: { total_items: number; total_credit: number; lines_count: number };
  cart: ReturnCartItem[];
  clientName: string;
  onDone: () => void;
}) {
  return (
    <div className="max-w-lg mx-auto px-4 py-10 space-y-6 text-center" dir="rtl">
      <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
        <CheckCircle2 className="w-8 h-8 text-green-600" />
      </div>
      <h1 className="text-xl font-bold text-gray-900">תעודת ההחזרה נשלחה בהצלחה!</h1>
      <p className="text-sm text-gray-500">
        הוחזרו {result.total_items} פריטים ({result.lines_count} שורות) עבור {clientName}
      </p>
      <p className="text-lg font-bold text-gray-900">סה״כ זיכוי: {formatPrice(result.total_credit)}</p>

      <div className="bg-gray-50 rounded-2xl p-4 space-y-2 text-right">
        <p className="text-xs font-semibold text-gray-500 mb-2">פירוט:</p>
        {cart.map(item => (
          <div key={item.product.id} className="flex items-center justify-between text-sm">
            <span className="text-gray-700 truncate flex-1">{item.product.name}</span>
            <span className="text-gray-500 shrink-0 mr-2">×{item.qty}</span>
            <span className="text-gray-900 font-semibold shrink-0">{formatPrice(item.product.price * item.qty)}</span>
          </div>
        ))}
      </div>

      <button onClick={onDone}
        className="w-full py-3 rounded-xl bg-orange-500 text-white font-bold text-sm
                   hover:bg-orange-600 transition-colors active:scale-[0.98]">
        סיום — חזרה לקטלוג
      </button>
    </div>
  );
}

// ─── Client Selector ─────────────────────────────────────────────────────────

function ClientSelector({
  clients, onSelect, agentName,
}: {
  clients: AgentClientRecord[];
  onSelect: (client: AgentClientRecord) => void;
  agentName: string | null;
}) {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter(c =>
      c.name.toLowerCase().includes(q) || (c.phone ?? '').includes(q) || (c.email ?? '').toLowerCase().includes(q),
    );
  }, [clients, search]);

  return (
    <div className="max-w-md mx-auto px-4 py-8 space-y-5" dir="rtl">
      <div className="text-center space-y-1">
        <RotateCcw className="w-8 h-8 mx-auto text-orange-500" />
        <h1 className="text-lg font-bold text-gray-900">תעודת החזרה</h1>
        {agentName && <p className="text-sm text-gray-500">שלום {agentName}, בחר לקוח:</p>}
      </div>

      <div className="relative">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input type="search" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="חפש לקוח…"
          className="w-full pr-9 pl-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm
                     focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-transparent
                     placeholder:text-gray-400" />
      </div>

      <div className="space-y-2 max-h-[60vh] overflow-y-auto">
        {filtered.length === 0 && (
          <p className="text-center text-sm text-gray-400 py-8">לא נמצאו לקוחות</p>
        )}
        {filtered.map(c => (
          <button key={c.id} onClick={() => onSelect(c)}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-100
                       bg-white hover:border-orange-300 hover:bg-orange-50 transition-colors text-right">
            <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
              <Building2 className="w-5 h-5 text-orange-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{c.name}</p>
              {c.phone && <p className="text-xs text-gray-400">{c.phone}</p>}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// ─── MAIN PAGE ──────────────────────────────────────────────────────────────
// ═════════════════════════════════════════════════════════════════════════════

type ViewMode = 'grid' | 'list';
type Screen = 'selectClient' | 'browse' | 'success';

export function B2BReturnCatalogPage() {
  const { user, loading: authLoading } = useAuth();
  const { session } = useB2BUser();

  const isAgent = session?.role === 'agent';
  const companyId = session?.activeCompanyId ?? null;
  const ctxCompanyIds: string[] = useMemo(
    () => session?.allowedCompanyIds ?? [],
    [session?.allowedCompanyIds?.join(',')],  // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ── Returns enabled check ────────────────────────────────────────────────
  const [returnsEnabled, setReturnsEnabled] = useState<boolean | null>(null);
  useEffect(() => {
    if (!companyId) return;
    supabase
      .from('company_settings')
      .select('b2b_returns_enabled')
      .eq('company_id', companyId)
      .maybeSingle()
      .then(({ data }) => {
        setReturnsEnabled(data?.b2b_returns_enabled !== false);
      });
  }, [companyId]);

  // ── Screen / client ──────────────────────────────────────────────────────
  const [screen, setScreen] = useState<Screen>('selectClient');
  const [selectedClient, setSelectedClient] = useState<AgentClientRecord | null>(null);

  const agentClients = useMemo(() => {
    if (!isAgent || !session) return [];
    const records = session.agentClientRecords ?? [];
    const activeComp = session.activeCompanyId;
    if (activeComp && (session.availableCompanies?.length ?? 0) > 1) {
      return records.filter(c => c.company_id === activeComp);
    }
    return records;
  }, [isAgent, session]);

  useEffect(() => {
    if (!session) return;
    if (session.activeClientRecord && !selectedClient) {
      setSelectedClient(session.activeClientRecord as AgentClientRecord);
      setScreen('browse');
    } else if (!isAgent && !selectedClient && ctxCompanyIds.length > 0) {
      setScreen('browse');
    }
  }, [session?.activeClientRecord?.id, selectedClient, isAgent, ctxCompanyIds.length > 0]);

  // ── Products + stock ─────────────────────────────────────────────────────
  const [products, setProducts] = useState<ReturnProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [view, setView] = useState<ViewMode>('grid');

  // Supplier & group filters
  const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);
  const [selectedSupplierId, setSelectedSupplierId] = useState<string | null>(null);
  const [filterGroups, setFilterGroups] = useState<GroupOption[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<string>>(new Set());

  const [pendingQty, setPendingQty] = useState<Record<string, number>>({});
  const getPending = (id: string) => pendingQty[id] ?? 1;
  const setPending = (id: string, n: number) =>
    setPendingQty(prev => ({ ...prev, [id]: Math.max(1, n) }));

  // VAT
  const [vatRateByCompany, setVatRateByCompany] = useState<Record<string, number>>({});
  const isVatLiable = session?.activeClientRecord?.is_vat_liable ?? true;

  useEffect(() => {
    if (ctxCompanyIds.length === 0) return;
    supabase.rpc('get_company_vat_rates', { p_company_ids: ctxCompanyIds })
      .then(({ data }) => {
        if (!data) return;
        const map: Record<string, number> = {};
        for (const r of data as { company_id: string; vat_rate: number | null }[]) {
          if (r.vat_rate != null) map[r.company_id] = Number(r.vat_rate);
        }
        setVatRateByCompany(map);
      });
  }, [ctxCompanyIds]);

  // Supplier names
  useEffect(() => {
    if (ctxCompanyIds.length === 0) return;
    supabase.from('companies').select('id, legal_name').in('id', ctxCompanyIds).order('legal_name')
      .then(({ data }) => {
        if (!data) return;
        setSuppliers((data as { id: string; legal_name: string }[]).map(r => ({
          id: r.id, name: r.legal_name ?? r.id,
        })));
      });
  }, [ctxCompanyIds.join(',')]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select single supplier
  useEffect(() => {
    if (ctxCompanyIds.length === 1) setSelectedSupplierId(ctxCompanyIds[0] ?? null);
    else setSelectedSupplierId(null);
    setSelectedGroupIds(new Set());
    setFilterGroups([]);
  }, [ctxCompanyIds.join(',')]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Load filter groups per supplier
  useEffect(() => {
    if (!selectedSupplierId) { setFilterGroups([]); setSelectedGroupIds(new Set()); return; }
    supabase.rpc('get_b2b_supplier_filter_groups', { p_company_id: selectedSupplierId })
      .then(({ data }) => {
        setFilterGroups((data ?? []).map((r: any) => ({ id: r.id, name: r.name, parent_id: r.parent_id ?? null })));
        setSelectedGroupIds(new Set());
      });
  }, [selectedSupplierId]);

  const selectedGroupIdsKey = useMemo(
    () => Array.from(selectedGroupIds).sort().join(','),
    [selectedGroupIds],
  );

  // ── Load products + warehouse balances ───────────────────────────────────
  const prevCtxKey = useRef('');
  const fallbackAttempted = useRef(false);

  const resolveCompanyIds = useCallback(async (): Promise<string[]> => {
    if (!user) return [];
    const orParts = [
      user.email ? `email.eq.${user.email}` : null,
      user.phone ? `phone.eq.${user.phone}` : null,
    ].filter(Boolean).join(',');
    if (!orParts) return [];
    const { data } = await supabase
      .from('inventory_clients').select('company_id').eq('b2b_allowed', true).or(orParts);
    if (!data || data.length === 0) return [];
    return [...new Set((data as { company_id: string }[]).map(r => r.company_id))];
  }, [user]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { setLoading(false); return; }
    if (screen !== 'browse') return;

    let cancelled = false;

    async function run() {
      const ctxKey = ctxCompanyIds.join(',');
      if (ctxKey !== prevCtxKey.current) {
        prevCtxKey.current = ctxKey;
        fallbackAttempted.current = false;
      }

      let ids = ctxCompanyIds;
      if (ids.length === 0 && !fallbackAttempted.current) {
        fallbackAttempted.current = true;
        ids = await resolveCompanyIds();
      }
      if (cancelled) return;

      setLoading(true);
      setError(null);

      try {
        const targetIds = selectedSupplierId ? ids.filter(id => id === selectedSupplierId) : ids;
        if (targetIds.length === 0) {
          if (!cancelled) { setProducts([]); setLoading(false); }
          return;
        }

        const groupParam = selectedGroupIds.size > 0 ? Array.from(selectedGroupIds) : null;

        // Get the client's warehouse ID
        const warehouseId = selectedClient?.consignment_warehouse_id ?? null;

        // Fetch catalog + warehouse balances in parallel
        const catalogPromise = supabase
          .rpc('get_b2b_catalog', {
            p_company_ids: targetIds,
            ...(groupParam ? { p_group_ids: groupParam } : {}),
          }).limit(10000);

        const balancesPromise = (warehouseId && companyId)
          ? supabase.rpc('b2b_agent_inventory_balances', {
              p_company_id: companyId,
              p_warehouse_id: warehouseId,
            })
          : Promise.resolve({ data: null, error: null });

        const [catalogRes, balancesRes] = await Promise.all([catalogPromise, balancesPromise]);
        if (cancelled) return;

        if (catalogRes.error) throw catalogRes.error;
        if (balancesRes.error) throw balancesRes.error;

        // Build stock map: product_id → balance in client warehouse
        const stockMap = new Map<string, number>();
        if (balancesRes.data) {
          for (const row of balancesRes.data as any[]) {
            const qty = Number(row.calculated_quantity ?? 0);
            if (qty > 0) stockMap.set(row.product_id, qty);
          }
        }

        // Apply pricelist overrides
        const priceOverrideMap = new Map<string, number>();
        const pricelistByCompany = session?.pricelistByCompany ?? {};
        const companiesWithPricelist = targetIds
          .map(cid => ({ companyId: cid, pricelistId: pricelistByCompany[cid] }))
          .filter((x): x is { companyId: string; pricelistId: string } => !!x.pricelistId);

        if (companiesWithPricelist.length > 0) {
          const byPricelist = new Map<string, string[]>();
          for (const { companyId: cid, pricelistId } of companiesWithPricelist) {
            const arr = byPricelist.get(pricelistId) ?? [];
            arr.push(cid);
            byPricelist.set(pricelistId, arr);
          }
          const priceResults = await Promise.all(
            Array.from(byPricelist.entries()).map(([pricelistId, cids]) =>
              supabase.rpc('get_b2b_pricelist_prices', { p_company_ids: cids, p_pricelist_id: pricelistId }),
            ),
          );
          if (!cancelled) {
            for (const result of priceResults) {
              if (result.data) {
                for (const row of result.data as any[]) {
                  if (row.price_after_discount != null) {
                    priceOverrideMap.set(row.product_id, Number(row.price_after_discount));
                  }
                }
              }
            }
          }
        }

        if (!cancelled) {
          // Only show products that have stock in the client's warehouse (when warehouse exists)
          const catalogData = (catalogRes.data ?? []) as any[];
          const mapped: ReturnProduct[] = catalogData
            .map((r: any) => ({
              id: r.id,
              company_id: r.company_id,
              sku: r.sku ?? '',
              name: r.name ?? 'מוצר',
              price: priceOverrideMap.get(r.id) ?? Number(r.price ?? 0),
              image_url: r.image_url ?? null,
              description: r.description ?? null,
              group_name: r.group_name ?? null,
              supplier_name: r.supplier_name ?? null,
              stock_qty: stockMap.get(r.id) ?? 0,
            }));

          // If we have a warehouse, filter to only products with stock
          if (warehouseId) {
            setProducts(mapped.filter(p => p.stock_qty > 0));
          } else {
            setProducts(mapped);
          }
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'שגיאה בטעינת המוצרים');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, screen, ctxCompanyIds.join(','), selectedSupplierId, selectedGroupIdsKey,
      resolveCompanyIds, selectedClient?.consignment_warehouse_id, companyId]);

  // ── Filtered by search ───────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter(p => p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q));
  }, [products, search]);

  // ── Return Cart (local state) ────────────────────────────────────────────
  const [cart, setCart] = useState<ReturnCartItem[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<{ total_items: number; total_credit: number; lines_count: number } | null>(null);
  const [submittedCart, setSubmittedCart] = useState<ReturnCartItem[]>([]);

  const [stockWarning, setStockWarning] = useState<string | null>(null);

  const addToReturnCart = useCallback((product: ReturnProduct, qty: number) => {
    setCart(prev => {
      const existing = prev.find(i => i.product.id === product.id);
      const currentQty = existing?.qty ?? 0;
      const newTotal = currentQty + qty;

      if (newTotal > product.stock_qty) {
        setStockWarning(`לא ניתן להחזיר יותר מ-${product.stock_qty} יחידות של "${product.name}" — זו הכמות שנמצאת במלאי`);
        setTimeout(() => setStockWarning(null), 4000);
        if (currentQty >= product.stock_qty) return prev;
        const allowed = product.stock_qty - currentQty;
        if (existing) {
          return prev.map(i => i.product.id === product.id ? { ...i, qty: product.stock_qty } : i);
        }
        return [...prev, { product, qty: allowed, reason: '' }];
      }

      if (existing) {
        return prev.map(i => i.product.id === product.id ? { ...i, qty: newTotal } : i);
      }
      return [...prev, { product, qty, reason: '' }];
    });
  }, []);

  const setCartQty = useCallback((productId: string, qty: number) => {
    setCart(prev => {
      const item = prev.find(i => i.product.id === productId);
      if (!item) return prev;
      if (qty > item.product.stock_qty) {
        setStockWarning(`לא ניתן להחזיר יותר מ-${item.product.stock_qty} יחידות — זו הכמות שנמצאת במלאי`);
        setTimeout(() => setStockWarning(null), 4000);
        return prev;
      }
      if (qty <= 0) return prev.filter(i => i.product.id !== productId);
      return prev.map(i => i.product.id === productId ? { ...i, qty } : i);
    });
  }, []);

  const setCartReason = useCallback((productId: string, reason: string) => {
    setCart(prev => prev.map(i => i.product.id === productId ? { ...i, reason } : i));
  }, []);

  const removeFromCart = useCallback((productId: string) => {
    setCart(prev => prev.filter(i => i.product.id !== productId));
  }, []);

  const clearCart = useCallback(() => { setCart([]); }, []);

  const totalItems = cart.reduce((s, i) => s + i.qty, 0);
  const totalPrice = cart.reduce((s, i) => s + i.product.price * i.qty, 0);

  // ── Submit ───────────────────────────────────────────────────────────────
  const { remindIfNeeded: remindTerms } = useB2BTerms();
  const handleSubmit = useCallback(async (instant: boolean) => {
    if (!companyId || !selectedClient || cart.length === 0) return;
    await remindTerms();
    setSubmitting(true);
    setSubmitError(null);
    try {
      const lines = cart.map(item => ({
        product_id: item.product.id,
        qty: item.qty,
        reason: item.reason.trim() || null,
        order_type: 'auto',
      }));

      const { data, error: rpcErr } = await supabase.rpc('submit_bulk_return', {
        p_company_id: companyId,
        p_client_id: selectedClient.id,
        p_lines: lines,
        p_instant: instant,
      });

      if (rpcErr) throw new Error(rpcErr.message);
      if (!data || !data.rma_ids) throw new Error('שגיאה בתוצאת ההחזרה');

      setSubmittedCart([...cart]);
      setResult({
        total_items: Number(data.total_items ?? 0),
        total_credit: Number(data.total_credit ?? 0),
        lines_count: Number(data.lines_count ?? 0),
      });
      setCartOpen(false);
      setScreen('success');
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'שגיאה בשליחת ההחזרה');
    } finally {
      setSubmitting(false);
    }
  }, [companyId, selectedClient, cart, remindTerms]);

  const handleDone = useCallback(() => {
    setResult(null);
    setSubmittedCart([]);
    setCart([]);
    setScreen('browse');
  }, []);

  const handleSelectClient = useCallback((client: AgentClientRecord) => {
    setSelectedClient(client);
    setCart([]);
    setProducts([]);
    setScreen('browse');
  }, []);

  const handleChangeClient = useCallback(() => {
    setSelectedClient(null);
    setCart([]);
    setProducts([]);
    setScreen('selectClient');
  }, []);

  // ── Add handler ──────────────────────────────────────────────────────────
  const handleAdd = (product: ReturnProduct) => {
    addToReturnCart(product, getPending(product.id));
    setPending(product.id, 1);
  };

  // ── Product detail modal ─────────────────────────────────────────────────
  const [detailProduct, setDetailProduct] = useState<ReturnProduct | null>(null);
  const [detailImages, setDetailImages] = useState<{ image_url: string; alt_text: string | null; position: number }[]>([]);

  const openDetail = useCallback(async (product: ReturnProduct) => {
    setDetailProduct(product);
    setDetailImages([]);
    const { data } = await supabase
      .from('inventory_product_images')
      .select('image_url, alt_text, position')
      .eq('product_id', product.id)
      .order('position');
    if (data && data.length > 0) setDetailImages(data);
  }, []);

  const handleSupplierChange = (id: string | null) => { setSelectedSupplierId(id); };

  // ── Redirects ────────────────────────────────────────────────────────────
  if (returnsEnabled === false) return <Navigate to="/b2b/orders" replace />;

  if (screen === 'success' && result) {
    return (
      <SuccessScreen
        result={result}
        cart={submittedCart}
        clientName={selectedClient?.name ?? ''}
        onDone={handleDone}
      />
    );
  }

  if (screen === 'selectClient' && isAgent) {
    return (
      <ClientSelector
        clients={agentClients}
        onSelect={handleSelectClient}
        agentName={session?.agentRecord?.name ?? null}
      />
    );
  }

  // ── Browse (catalog view) ────────────────────────────────────────────────
  return (
    <div className="max-w-5xl mx-auto px-4 py-5 space-y-4 pb-28">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <RotateCcw className="w-5 h-5 text-orange-500" />
          <div>
            <h1 className="text-lg font-bold text-gray-900">תעודת החזרה</h1>
            {selectedClient && (
              <p className="text-xs text-gray-400">
                {selectedClient.name}
              </p>
            )}
          </div>
        </div>
        {isAgent && selectedClient && (
          <button onClick={handleChangeClient}
            className="text-xs text-orange-600 hover:text-orange-700 font-semibold transition-colors">
            החלף לקוח
          </button>
        )}
      </div>

      {/* Stock warning toast */}
      {stockWarning && (
        <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 border border-amber-200
                       rounded-xl text-sm text-amber-800 animate-pulse">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {stockWarning}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[160px]">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input type="search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="חפש לפי שם או מק״ט…"
            className="w-full pr-9 pl-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm
                       focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-transparent
                       placeholder:text-gray-400" />
        </div>

        {ctxCompanyIds.length > 1 && (
          <SupplierFilter suppliers={suppliers} selectedSupplierId={selectedSupplierId}
            onChange={handleSupplierChange} />
        )}

        {selectedSupplierId && filterGroups.length > 0 && (
          <GroupFilter groups={filterGroups} selectedGroupIds={selectedGroupIds}
            onChange={setSelectedGroupIds} />
        )}

        <div className="flex items-center gap-1 p-1 bg-white border border-gray-200 rounded-xl shrink-0">
          <button onClick={() => setView('grid')}
            className={`p-2 rounded-lg transition-colors ${view === 'grid' ? 'bg-orange-500 text-white' : 'text-gray-400 hover:text-gray-600'}`}>
            <LayoutGrid className="w-4 h-4" />
          </button>
          <button onClick={() => setView('list')}
            className={`p-2 rounded-lg transition-colors ${view === 'list' ? 'bg-orange-500 text-white' : 'text-gray-400 hover:text-gray-600'}`}>
            <List className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Active filter chips */}
      {(selectedClient || selectedSupplierId || selectedGroupIds.size > 0) && (
        <div className="flex items-center gap-2 flex-wrap">
          {selectedClient && (
            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full
                           bg-orange-50 text-orange-700 text-xs font-semibold border border-orange-100">
              <Building2 className="w-3 h-3 text-orange-500" />
              מחזיר עבור: {selectedClient.name}
            </span>
          )}
          {selectedSupplierId && suppliers.find(s => s.id === selectedSupplierId) && ctxCompanyIds.length > 1 && (
            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full
                           bg-orange-100 text-orange-700 text-xs font-semibold">
              <Building2 className="w-3 h-3" />
              {suppliers.find(s => s.id === selectedSupplierId)!.name}
              <button onClick={() => handleSupplierChange(null)}
                className="mr-0.5 opacity-60 hover:opacity-100 transition-opacity">
                <XIcon className="w-3 h-3" />
              </button>
            </span>
          )}
          {Array.from(selectedGroupIds).map(gid => {
            const g = filterGroups.find(x => x.id === gid);
            if (!g) return null;
            return (
              <span key={gid} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full
                             bg-amber-50 text-amber-700 text-xs font-semibold border border-amber-100">
                <Filter className="w-3 h-3 text-amber-500" />
                {g.name}
                <button onClick={() => { const next = new Set(selectedGroupIds); next.delete(gid); setSelectedGroupIds(next); }}
                  className="mr-0.5 opacity-60 hover:opacity-100 transition-opacity">
                  <XIcon className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Results count */}
      {!loading && !error && (
        <p className="text-xs text-gray-400">
          {filtered.length === products.length
            ? `${products.length} מוצרים`
            : `${filtered.length} תוצאות מתוך ${products.length} מוצרים`}
        </p>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-20 gap-3 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span className="text-sm">טוען מוצרים…</span>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-start gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-2xl text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">שגיאה בטעינת המוצרים</p>
            <p className="text-xs mt-0.5 text-red-600">{error}</p>
          </div>
        </div>
      )}

      {/* Empty */}
      {!loading && !error && filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 gap-3 text-gray-400">
          <ShoppingBag className="w-14 h-14 text-gray-200" />
          <p className="text-sm font-medium">
            {products.length === 0 ? 'אין מוצרים עם מלאי להחזרה' : 'לא נמצאו תוצאות'}
          </p>
          {search && (
            <button onClick={() => setSearch('')} className="text-xs text-orange-600 hover:underline">
              נקה חיפוש
            </button>
          )}
        </div>
      )}

      {/* Grid */}
      {!loading && !error && view === 'grid' && filtered.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {filtered.map(p => (
            <GridCard key={p.id} product={p}
              qty={getPending(p.id)}
              onQtyChange={n => setPending(p.id, Math.min(n, p.stock_qty > 0 ? p.stock_qty : n))}
              onAdd={() => handleAdd(p)}
              vatRate={vatRateByCompany[p.company_id] ?? 0}
              isVatLiable={isVatLiable}
              onDetail={() => openDetail(p)} />
          ))}
        </div>
      )}

      {/* List */}
      {!loading && !error && view === 'list' && filtered.length > 0 && (
        <div className="space-y-2">
          {filtered.map(p => (
            <ListRow key={p.id} product={p}
              qty={getPending(p.id)}
              onQtyChange={n => setPending(p.id, Math.min(n, p.stock_qty > 0 ? p.stock_qty : n))}
              onAdd={() => handleAdd(p)}
              vatRate={vatRateByCompany[p.company_id] ?? 0}
              isVatLiable={isVatLiable}
              onDetail={() => openDetail(p)} />
          ))}
        </div>
      )}

      {/* Product Detail Modal */}
      {detailProduct && (
        <ProductDetailModal
          product={detailProduct}
          images={detailImages}
          onClose={() => setDetailProduct(null)}
          qty={getPending(detailProduct.id)}
          onQtyChange={n => setPending(detailProduct.id, n)}
          onAdd={() => handleAdd(detailProduct)}
          vatRate={vatRateByCompany[detailProduct.company_id] ?? 0}
          isVatLiable={isVatLiable} />
      )}

      {/* Floating cart bar */}
      {!cartOpen && (
        <FloatingCartBar totalItems={totalItems} totalPrice={totalPrice}
          onClick={() => setCartOpen(true)} />
      )}

      {/* Expanded cart */}
      {cartOpen && (
        <ExpandedReturnCart
          items={cart}
          onClose={() => setCartOpen(false)}
          onSetQty={setCartQty}
          onSetReason={setCartReason}
          onRemove={removeFromCart}
          onClear={clearCart}
          onSubmit={handleSubmit}
          submitting={submitting}
          submitError={submitError}
          vatRate={vatRateByCompany[companyId ?? ''] ?? 0}
          isVatLiable={isVatLiable} />
      )}
    </div>
  );
}
